import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const HealthBar = ({ health, average }) => {
  const barRef = useRef();

  useEffect(() => {
    const width = 160;
    const height = 24;

    const svg = d3.select(barRef.current)
      .attr('width', width)
      .attr('height', height);

    svg.selectAll('*').remove();

    svg.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#e5e7eb');

    svg.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', (health / 100) * width)
      .attr('height', height)
      .attr('fill', '#032FCE');

    svg.append('line')
      .attr('x1', (average / 100) * width)
      .attr('x2', (average / 100) * width)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#7BA5FF')
      .attr('stroke-width', 2);

    svg.append('text')
      .text(`${health}%`)
      .attr('x', 4)
      .attr('y', height / 2 + 5)
      .attr('fill', 'white')
      .attr('font-size', '13px')
      .attr('font-weight', 'normal');
  }, [health, average]);

  return <svg ref={barRef}></svg>;
};

const PredictedRiskDetails = ({ data = [] }) => {
  const [tableData, setTableData] = useState(data);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedComponents, setSelectedComponents] = useState([]); // Track selected components

  useEffect(() => {
    setTableData(data);
    setSelectedComponents([]); // Reset selections when data changes
  }, [data]);

  const onSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedData = [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setTableData(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '⇅';
  };

  const handleCheckboxChange = (componentName) => {
    setSelectedComponents((prev) =>
      prev.includes(componentName)
        ? prev.filter((name) => name !== componentName)
        : [...prev, componentName]
    );
  };

  const handleSelectAll = () => {
    if (selectedComponents.length === tableData.length) {
      setSelectedComponents([]); // Deselect all
    } else {
      setSelectedComponents(tableData.map((comp) => comp.component_name)); // Select all
    }
  };

  const handleCreateWorkOrder = () => {
    if (selectedComponents.length === 0) return; // Prevent submission if no components selected

    setIsSubmitting(true);
    setSuccessMessage('Work order submitted successfully');

    // Placeholder for API call with selected components
    console.log('Submitting work order for components:', selectedComponents);

    setTimeout(() => {
      setSuccessMessage('');
      setIsSubmitting(false);
      setSelectedComponents([]); // Reset selections after submission
    }, 3000);
  };

  return (
    <div className="mt-6 w-full">
      <h2 className="mb-2 v_health_head_text_1">
        Predicted Risk Details
      </h2>
      <div className="rounded border border-gray-300 bg-white overflow-x-auto p-4 mt-2">
        <div className="min-w-[590px] min-h-[445px]">
          <table className="w-full table-auto">
            <thead className="bg-white text-left text-sm font-semibold">
              <tr>
                <th className="px-4 py-2 w-12">
                  <div className="flex items-center h-full">
                    <input
                      type="checkbox"
                      checked={selectedComponents.length === tableData.length && tableData.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-2 v_health_head_text cursor-pointer" onClick={() => onSort('component_name')}>
                  Component <span>{getSortIcon('component_name')}</span>
                </th>
                <th className="px-4 py-2 v_health_head_text cursor-pointer" onClick={() => onSort('component_health')}>
                  Health % <br />(vs. Fleet Average) <span>{getSortIcon('component_health')}</span>
                </th>
                <th className="px-4 py-2 v_health_head_text cursor-pointer" onClick={() => onSort('component_failure_expected')}>
                  Time Until Failure <span>{getSortIcon('component_failure_expected')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm border border-gray-200 v_health_tbody_bgcolor">
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-gray-500">
                    No Risk Forseen!
                  </td>
                </tr>
              ) : (
                tableData.map((comp, idx) => (
                  <tr key={idx} className="border-t border-gray-200">
                    <td className="px-4 py-2 w-12">
                      <input
                        type="checkbox"
                        checked={selectedComponents.includes(comp.component_name)}
                        onChange={() => handleCheckboxChange(comp.component_name)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2 v_health_col_text whitespace-nowrap">{comp.component_name}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <HealthBar health={comp.component_health} average={comp.component_fleet_average} />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center space-x-1">
                        <i
                          className={`ti ti-circle-filled font-bold text-xl text-yellow-500`}
                        />
                        <span className={`font-bold table_warning_bg_color`}>
                          {comp.component_failure_expected} {parseInt(comp.component_failure_expected) > 1 ? 'Weeks' : 'Week'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <table className={`w-full table-auto pt-4 ${tableData.length === 0 ? 'hidden' : ''}`}>
            <tbody className="">
              <tr>
                <td colSpan="4" className="text-center">
                  <div className="pt-4 inline-block">
                    <button
                      className="buttonWo"
                      onClick={handleCreateWorkOrder}
                      disabled={isSubmitting || selectedComponents.length === 0}
                    >
                      <span className="text-wrapper">Submit Work Order</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right">
                  <div className="pt-4">
                    <div className="flex justify-center items-center h-full">
                      <span className={`text-green-600 font-semibold text-sm ${!successMessage ? 'invisible' : ''}`}>
                        {successMessage || ''}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
        </div>
      </div>
    </div>
  );
};

export default PredictedRiskDetails;